/**
 * Feedback Detail Modal Component
 *
 * Modal for viewing feedback details and marking as actioned
 */

import React, { useState, useEffect } from 'react';
import { advisorApiService } from '../services/advisor-api';
import type { StudentFeedbackRecord } from '../types';
import '../styles/FeedbackDetailModal.css';

interface FeedbackDetailModalProps {
  feedback: StudentFeedbackRecord;
  onClose: () => void;
  onSuccess: () => void;
}

type TabType = 'overview' | 'support';

interface AgentReply {
  reply: string;
  agent_name: string;
}

export const FeedbackDetailModal: React.FC<FeedbackDetailModalProps> = ({
  feedback,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [adminComment, setAdminComment] = useState(feedback.admin_comment || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Support tab state
  const [agentReply, setAgentReply] = useState('');
  const [replies, setReplies] = useState<Record<string, AgentReply>>({});
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!adminComment.trim()) {
      setError('Admin comment is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await advisorApiService.updateFeedbackAction({
        id: feedback.id,
        admin_comment: adminComment.trim(),
      });

      if (response.error) {
        setError(response.error);
        setIsSubmitting(false);
        return;
      }

      // Success - stay in Support tab and trigger parent refresh
      feedback.is_actioned = true;
      feedback.admin_comment = adminComment.trim();
      setActiveTab('support');
      setIsSubmitting(false);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update feedback');
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const loadAgentReplies = React.useCallback(async () => {
    setIsLoadingReplies(true);
    setError(null);

    try {
      const response = await advisorApiService.getAgentReplies(feedback.id);

      if (response.error) {
        setError(response.error);
        return;
      }

      setReplies(response.data?.replies || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load replies');
    } finally {
      setIsLoadingReplies(false);
    }
  }, [feedback.id]);

  // Load agent replies when Support tab is opened
  useEffect(() => {
    if (activeTab === 'support') {
      loadAgentReplies();
    }
  }, [activeTab, loadAgentReplies]);

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!agentReply.trim()) {
      setError('Reply cannot be empty');
      return;
    }

    setIsSendingReply(true);
    setError(null);

    try {
      const response = await advisorApiService.sendAgentReply({
        feedback_id: feedback.id,
        agent_reply: agentReply.trim(),
        agent_name: 'admin',
      });

      if (response.error) {
        setError(response.error);
        setIsSendingReply(false);
        return;
      }

      // Success - clear the reply input and reload replies
      setAgentReply('');
      await loadAgentReplies();
      setIsSendingReply(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reply');
      setIsSendingReply(false);
    }
  };

  return (
    <div className="feedback-detail-overlay" onClick={onClose}>
      <div className="feedback-detail-content" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-detail-header">
          <h2>Feedback Details</h2>
          <button
            className="feedback-detail-close"
            onClick={onClose}
            disabled={isSubmitting || isSendingReply}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="feedback-detail-tabs">
          <button
            className={`feedback-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            disabled={isSubmitting || isSendingReply}
          >
            Overview
          </button>
          <button
            className={`feedback-tab ${activeTab === 'support' ? 'active' : ''}`}
            onClick={() => setActiveTab('support')}
            disabled={isSubmitting || isSendingReply}
          >
            Support
          </button>
        </div>

        <div className="feedback-detail-body">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              <div className="feedback-detail-section">
                <h3>Feedback ID</h3>
                <p className="feedback-id">{feedback.id}</p>
              </div>

              <div className="feedback-detail-section">
                <h3>Created</h3>
                <p>{formatDate(feedback.created_time)}</p>
              </div>

              <div className="feedback-detail-section">
                <h3>Name</h3>
                <p>{feedback.name}</p>
              </div>

              <div className="feedback-detail-section">
                <h3>UNI</h3>
                <p>{feedback.uni}</p>
              </div>

              <div className="feedback-detail-section">
                <h3>Email</h3>
                <p>{feedback.email}</p>
              </div>

              <div className="feedback-detail-section">
                <h3>Advising Email</h3>
                <p>{feedback.advising_email}</p>
              </div>

              {feedback.phone_number && (
                <div className="feedback-detail-section">
                  <h3>Phone Number</h3>
                  <p>{feedback.phone_number}</p>
                </div>
              )}

              <div className="feedback-detail-section">
                <h3>Feedback</h3>
                <div className="feedback-detail-text">{feedback.feedback}</div>
              </div>

              <div className="feedback-detail-section">
                <h3>Status</h3>
                <p>
                  <span className={`status-badge ${feedback.is_actioned ? 'actioned' : 'pending'}`}>
                    {feedback.is_actioned ? 'Actioned' : 'Pending'}
                  </span>
                </p>
              </div>

              {!feedback.is_actioned && (
                <form onSubmit={handleSubmit}>
                  <div className="feedback-detail-section">
                    <h3>
                      Admin Comment <span className="required">*</span>
                    </h3>
                    <textarea
                      value={adminComment}
                      onChange={(e) => setAdminComment(e.target.value)}
                      disabled={isSubmitting}
                      maxLength={1000}
                      rows={4}
                      placeholder="Enter your comment about the action taken..."
                      className="admin-comment-input"
                    />
                  </div>

                  {error && <div className="feedback-detail-error">{error}</div>}

                  <div className="feedback-detail-actions">
                    <button
                      type="button"
                      className="feedback-detail-button-secondary"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="feedback-detail-button-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'I have taken action'}
                    </button>
                  </div>
                </form>
              )}

              {feedback.is_actioned && feedback.admin_comment && (
                <div className="feedback-detail-section">
                  <h3>Admin Comment</h3>
                  <div className="feedback-detail-text">{feedback.admin_comment}</div>
                </div>
              )}
            </>
          )}

          {/* Support Tab */}
          {activeTab === 'support' && (
            <>
              <div className="feedback-detail-section">
                <h3>Reply History</h3>
                {isLoadingReplies ? (
                  <p>Loading replies...</p>
                ) : Object.keys(replies).length === 0 ? (
                  <p className="no-replies">No replies yet</p>
                ) : (
                  <div className="reply-list">
                    {Object.entries(replies)
                      .sort(
                        ([timestampA], [timestampB]) =>
                          new Date(timestampA).getTime() - new Date(timestampB).getTime()
                      )
                      .map(([timestamp, reply]) => (
                        <div key={timestamp} className="reply-item">
                          <div className="reply-header">
                            <span className="reply-agent">{reply.agent_name}</span>
                            <span className="reply-timestamp">{formatDate(timestamp)}</span>
                          </div>
                          <div className="reply-content">{reply.reply}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {!feedback.is_actioned && (
                <form onSubmit={handleSendReply}>
                  <div className="feedback-detail-section">
                    <h3>
                      Send Reply <span className="required">*</span>
                    </h3>
                    <p className="email-template-hint">
                      Your message will be wrapped in an email template. Type only your reply
                      message below:
                    </p>
                    <div className="email-preview-container">
                      <div className="email-preview">
                        <div className="email-preview-line">Hi &#123;Student Name&#125;,</div>
                        <div className="email-preview-line email-preview-spacer"></div>
                        <textarea
                          value={agentReply}
                          onChange={(e) => setAgentReply(e.target.value)}
                          disabled={isSendingReply}
                          maxLength={5000}
                          rows={6}
                          placeholder="Type your message here..."
                          className="admin-comment-input email-reply-input"
                        />
                        <div className="email-preview-line email-preview-spacer"></div>
                        <div className="email-preview-line">Best,</div>
                        <div className="email-preview-line">Columbia University CS Advisor</div>
                      </div>
                    </div>
                  </div>

                  {error && <div className="feedback-detail-error">{error}</div>}

                  <div className="feedback-detail-actions">
                    <button
                      type="button"
                      className="feedback-detail-button-secondary"
                      onClick={onClose}
                      disabled={isSendingReply}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="feedback-detail-button-primary"
                      disabled={isSendingReply || !agentReply.trim()}
                    >
                      {isSendingReply ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </form>
              )}

              {feedback.is_actioned && (
                <div className="feedback-detail-info">
                  <p className="actioned-notice">
                    This feedback has been marked as actioned. You can view reply history above, but
                    cannot send new replies.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
